/**
 * Export.
 *
 * Four formats, one dialog. The options that only apply to images are hidden
 * for the text formats rather than disabled, because a greyed-out control still
 * costs the reader a decision.
 */
import { useState } from 'react';
import { Button, Dialog, DialogFooter, Select, Slider, Toggle } from '../ui';
import type { FlowGraph } from '../../graph/model/types';
import type { Translator } from '../../i18n';
import { BRANDING, exportFileName } from '../../config/branding';
import {
  downloadBlob,
  downloadText,
  exportPng,
  exportSvg,
  resolveThemeColors,
  serializeDocument,
} from '../../export';
import type { Rect } from '../../graph/model/types';

export type ExportFormat = 'code' | 'json' | 'svg' | 'png';

export interface ExportDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly graph: FlowGraph;
  readonly source: string;
  readonly projectId: string;
  readonly projectName: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly visibleRect: Rect | null;
  readonly onShare: () => void;
  readonly onNotify: (message: string, tone: 'success' | 'error') => void;
  readonly t: Translator['t'];
}

export function ExportDialog(props: ExportDialogProps) {
  const { t, onNotify } = props;
  const [format, setFormat] = useState<ExportFormat>('svg');
  const [scale, setScale] = useState(2);
  const [margin, setMargin] = useState(24);
  const [transparent, setTransparent] = useState(false);
  const [visibleOnly, setVisibleOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const isImage = format === 'svg' || format === 'png';

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      switch (format) {
        case 'code': {
          const name = exportFileName(props.projectName, BRANDING.sourceExtension);
          downloadText(props.source, name, 'text/plain');
          onNotify(t('export.done', { fileName: name }), 'success');
          break;
        }
        case 'json': {
          const name = exportFileName(props.projectName, '.json');
          downloadText(
            serializeDocument({
              project: {
                id: props.projectId,
                name: props.projectName,
                source: props.source,
                createdAt: props.createdAt,
                updatedAt: props.updatedAt,
              },
            }),
            name,
            'application/json',
          );
          onNotify(t('export.done', { fileName: name }), 'success');
          break;
        }
        case 'svg': {
          const name = exportFileName(props.projectName, '.svg');
          downloadText(svgFor(props, { margin, transparent, visibleOnly }), name, 'image/svg+xml');
          onNotify(t('export.done', { fileName: name }), 'success');
          break;
        }
        case 'png': {
          const name = exportFileName(props.projectName, '.png');
          const blob = await exportPng(props.graph, {
            padding: margin,
            background: transparent ? null : 'var(--fs-diagram-bg)',
            colors: resolveThemeColors(),
            pixelRatio: scale,
            title: props.projectName,
            region: visibleOnly && props.visibleRect ? props.visibleRect : 'whole',
          });
          downloadBlob(blob, name);
          onNotify(t('export.done', { fileName: name }), 'success');
          break;
        }
      }
      props.onClose();
    } catch (error) {
      onNotify(
        `${t('export.failed')} ${error instanceof Error ? error.message : ''}`.trim(),
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title={t('export.title')}
      size="md"
      footer={
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              props.onShare();
            }}
          >
            {t('export.copyLink')}
          </Button>
          <Button variant="ghost" onClick={props.onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void run()}>
            {t('export.download')}
          </Button>
        </DialogFooter>
      }
    >
      <div className="flex flex-col gap-5">
        <Select
          label={t('export.format')}
          value={format}
          onChange={(value) => setFormat(value)}
          options={[
            { value: 'svg', label: t('export.svg') },
            { value: 'png', label: t('export.png') },
            { value: 'code', label: t('export.code') },
            { value: 'json', label: t('export.json') },
          ]}
        />

        {isImage ? (
          <>
            <Slider
              label={t('export.margin')}
              value={margin}
              min={0}
              max={120}
              step={4}
              onChange={setMargin}
              unit="px"
            />

            {format === 'png' ? (
              <Slider
                label={t('export.scale')}
                value={scale}
                min={1}
                max={4}
                step={1}
                onChange={setScale}
                format={(value) => t('export.scaleValue', { scale: value })}
              />
            ) : null}

            <Toggle
              label={t('export.transparentBackground')}
              checked={transparent}
              onChange={setTransparent}
            />

            <Toggle
              label={t('export.visibleAreaOnly')}
              description={props.visibleRect ? undefined : t('export.wholeDiagram')}
              disabled={!props.visibleRect}
              checked={visibleOnly && props.visibleRect !== null}
              onChange={setVisibleOnly}
            />
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function svgFor(
  props: ExportDialogProps,
  options: { margin: number; transparent: boolean; visibleOnly: boolean },
): string {
  return exportSvg(props.graph, {
    padding: options.margin,
    background: options.transparent ? null : 'var(--fs-diagram-bg)',
    colors: resolveThemeColors(),
    title: props.projectName,
    region: options.visibleOnly && props.visibleRect ? props.visibleRect : 'whole',
  });
}
